import openCase from 'api/openCase';
import { push } from 'connected-react-router';
import { providerToCountry } from 'mappers/providers';
import { call, put, select } from 'redux-saga/effects';
import caseCreationActions from 'redux/actions/caseCreationActions';
import { addGtmOpenCaseDataLayer } from 'redux/actions/gtmActions';
import { resetPictures } from 'redux/actions/pictureActions';
import {
  caseCreatedSelector,
  caseCreationSelector,
  decisionTreeDropOffContentSelector,
  decisionTreeDropOffSelector,
  decisionTreeSelector,
  isPropertyOccupancySelector,
} from 'redux/selectors/caseCreation';
import {
  currentLanguageSelector,
  partnerNameSelector,
  partnerSelector,
  selectProvider,
  selectShowPropertyOccupancy,
} from 'redux/selectors/config';
import { uploadedPicturesSelector } from 'redux/selectors/picture';
import { routes } from 'Routes/routesConfig';
import { isTheSameEnvironmentVariable } from 'utils/app-utils';
import { getRecaptchaToken } from 'utils/sagas-utils';
import { uploadPicturesToCMX } from '../picture/uploadPicturesToCMX';
import { migrateData } from './mapData';
import { addErrorAlert } from 'redux/actions/alertActions';
import {
  iCVCPolicyIdSelector,
  iCVCErrorSelector,
  iCVCEligibilityTokenSelector,
} from 'redux/selectors/icvc';
import applicationSpinnerActions from 'redux/actions/applicationSpinnerActions';
import { selectDTOption } from 'redux/selectors/config';

const { startLoad, stopLoad } = applicationSpinnerActions;

const { createCaseEnd, createCaseSuccess, createCaseFail } =
  caseCreationActions;

const RECAPTCHA_PUBLIC_KEY = process.env.REACT_APP_RECAPTCHA_PUBLIC_SECRET;
const TIME_TO_EXPIRE_PICTURE = 1620000;

function* getNextPageBasedOnICVC(status, decisionTreeStatus, isSoftFix) {
  const iCVCError = yield select(iCVCErrorSelector);
  switch (status) {
    case 'CREATED':
      if (isSoftFix) {
        return routes.DROPOFF_SUCCESS;
      }
      return ['EXCLUDED', 'OUT OF SCOPE'].includes(decisionTreeStatus)
        ? routes.CALLBACK
        : routes.SUCCESS;
    case 'ONGOING':
      if (!(iCVCError && Object.keys(iCVCError).length)) {
        return routes.CALLBACK;
      }
      return routes.CALLBACK_POLICY_ERROR;
    case 'EXCLUDED':
      return routes.EXCLUDED;
    default:
      yield put(
        addErrorAlert({ code: 'case_creation_dropoff-error_invalid_status' }),
      );
      return routes.ERROR;
  }
}

export const getNextPageBasedOnDecisionTree = dropOff => {
  switch (dropOff?.toLowerCase()) {
    case 'normal':
      return routes.EXCLUDED;
    case 'success':
      return routes.DROPOFF_SUCCESS;
    default:
      return routes.SUCCESS;
  }
};

function* getNextPage({
  decisionTreeStatus,
  dropOffType,
  isPropertyOccupancy,
  isSoftFix,
  shouldCheckOccupancy,
  status,
}) {
  let actualDropOffType = dropOffType;
  if (shouldCheckOccupancy && !isPropertyOccupancy) {
    actualDropOffType = 'normal';
  }
  if (isSoftFix) {
    actualDropOffType = 'success';
  }
  if (dropOffType || !status) {
    return getNextPageBasedOnDecisionTree(actualDropOffType);
  } else {
    return yield getNextPageBasedOnICVC(status, decisionTreeStatus, isSoftFix);
  }
}

function* getCreateCaseResponse(openCaseBody, recaptchaToken) {
  const currentResponse = yield select(caseCreatedSelector);
  if (currentResponse.caseReference) {
    return {
      result: currentResponse,
      status: 201,
    };
  } else {
    return yield call(openCase, {
      body: openCaseBody,
      recaptchaToken,
    });
  }
}

export const getAnswer = (type, value, answers) => {
  switch (type) {
    case 'boolean':
      return { answered_boolean: value === 'true' };
    case 'date':
    case 'date_time':
      return {
        answered_date_time: new Date(value).toISOString().split('.')[0] + 'Z',
      };
    case 'number':
      return { answered_number: Number(value) };
    case 'enum':
      const validAnswers = value.split('|');
      return {
        enumeration_answers: answers
          .filter(({ id }) => !!id) //CC returns ill formatted answers
          .map(answer => ({
            answer_id: answer.id,
            answer_text: ('' + answer.displayName).slice(0, 100),
            is_selected: validAnswers.includes(answer.id),
          }))
          .sort(({ is_selected }) => (is_selected ? -1 : 1))
          .slice(0, 20),
      };
    case 'location':
    case 'text':
      return { answered_text: value };
    default:
  }
};

const getQuestionType = type => {
  switch (type) {
    case 'text':
      return 'STRING';
    case 'date':
      return 'DATE_TIME';
    default:
      return type.toUpperCase();
  }
};

function createAllServices(services, checkUpService) {
  const allServices = [...(services || [])];
  if (checkUpService) {
    if (allServices.some(service => service.service_code === checkUpService)) {
      const checkUpServiceAlreadyPresent = allServices.find(
        service => service.service_code === checkUpService,
      );
      checkUpServiceAlreadyPresent.is_check_up = true;
    } else {
      const checkUpServiceFormatted = {
        questions_and_answers: JSON.parse(
          JSON.stringify(services[0]),
        ).questions_and_answers?.map(({ id, value }) => {
          if (id === 'SERVICES') {
            return { id, value: checkUpService };
          } else {
            return { id, value };
          }
        }),
        is_check_up: true,
        service_code: checkUpService,
      };
      allServices.push(checkUpServiceFormatted);
    }
  }
  return allServices;
}

const addNonDuplicatedNonUndefinedQuestions = (acc, question) =>
  question &&
  !acc.find(({ question_id }) => question_id === question.question_id)
    ? [...acc, question]
    : acc;

function* getQuestionsAndAnswers({
  checkUpService,
  optionPath,
  result: { questions },
  services,
}) {
  const dtOption = yield select(selectDTOption);
  if (dtOption === 'etl') {
    const questionsAndAnswers = optionPath.map(({ id, value }) => ({
      question: id,
      answer: value,
    }));
    return { questionsAndAnswers };
  } else {
    const ccMap = treeType => data => {
      const { id, value, questionText } = data;
      const questionMetaData = questions[treeType].find(
        ({ id: questionId }) => id === questionId,
      );
      if (!questionMetaData) {
        return false;
      }
      const { type, priority, answers, displayName, metadata } =
        questionMetaData;
      const response = getAnswer(type, value, answers);
      return {
        question_id: id,
        question_text:
          questionText ||
          displayName ||
          metadata.find(({ key }) => key === 'question')?.value ||
          id,
        question_type: getQuestionType(type),
        priority: priority || 0,
        ...response,
      };
    };
    const questionsAndAnswers = optionPath
      .map(ccMap('diagnosis'))
      .filter(question => question);
    const allServices = createAllServices(services, checkUpService);
    const formattedServices = allServices.map(
      ({ questions_and_answers, ...rest }) => ({
        ...rest,
        questions_and_answers: [...questions_and_answers, ...optionPath]
          .map(ccMap('services'))
          .reduce(addNonDuplicatedNonUndefinedQuestions, []),
      }),
    );
    return {
      questionsAndAnswers,
      services: formattedServices,
    };
  }
}
export function* createCaseSaga() {
  try {
    yield put(startLoad());
    const caseCreationData = yield select(caseCreationSelector);
    const policyId = yield select(iCVCPolicyIdSelector);
    const eligibilityToken = yield select(iCVCEligibilityTokenSelector);
    const decisionTree = yield select(decisionTreeSelector);
    const content = yield select(decisionTreeDropOffContentSelector);
    const picturesTobeRefreshedOnCMX = caseCreationData.pictures.filter(
      picture =>
        new Date() - new Date(picture.uploadedAt) > TIME_TO_EXPIRE_PICTURE,
    );
    if (picturesTobeRefreshedOnCMX.length) {
      yield* uploadPicturesToCMX({
        payload: { pictures: picturesTobeRefreshedOnCMX },
      });
      const refreshedPictures = yield select(uploadedPicturesSelector());
      refreshedPictures.forEach(refreshedPicture => {
        caseCreationData.pictures[
          caseCreationData.pictures.findIndex(
            picture => picture.name === refreshedPicture.name,
          )
        ].id = refreshedPicture.id;
      });
      yield put(resetPictures());
    }
    const provider = yield select(selectProvider);
    const partner_reference = yield select(partnerSelector);
    const partner_name = yield select(partnerNameSelector);
    const preferred_language = yield select(currentLanguageSelector);
    const country = providerToCountry[provider];
    const { questionsAndAnswers, services } = yield getQuestionsAndAnswers(
      decisionTree,
    );
    const openCaseBody = migrateData({
      caseCreationData,
      decisionTree,
      partner_reference,
      country,
      partner_name,
      policyId,
      eligibilityToken,
      preferred_language: preferred_language.substring(0, 2),
      questionsAndAnswers,
      services,
    });

    const isReCaptchaEnable = !isTheSameEnvironmentVariable(
      RECAPTCHA_PUBLIC_KEY,
      'RECAPTCHA_DISABLE',
    );

    const recaptchaToken = isReCaptchaEnable
      ? yield getRecaptchaToken()
      : 'RECAPTCHA_DISABLE';

    const dropOffType = yield select(decisionTreeDropOffSelector);
    const shouldCheckOccupancy = yield select(selectShowPropertyOccupancy);
    const isPropertyOccupancy = yield select(isPropertyOccupancySelector);
    const response = yield getCreateCaseResponse(openCaseBody, recaptchaToken);
    const {
      result: { caseId, caseGuid, caseReference, ...result },
      status,
    } = response;
    if (status === 201) {
      yield put(createCaseSuccess({ caseGuid, caseReference, content }));
      yield put(
        addGtmOpenCaseDataLayer({
          case_id: caseId,
          case_reference: caseReference,
          hashed_case_id: caseGuid,
        }),
      );
      const nextPage = yield getNextPage({
        dropOffType,
        shouldCheckOccupancy,
        isPropertyOccupancy,
        isSoftFix: decisionTree.result.isSoftFix,
        status: result.status,
        decisionTreeStatus: decisionTree.result.status,
      });
      yield put(push(nextPage));
      yield put(createCaseEnd());
    } else {
      yield put(createCaseFail(result));
      yield put(push(routes.ERROR));
    }
  } catch (error) {
    yield put(createCaseFail(error?.message || error));
    yield put(push(routes.ERROR));
  } finally {
    yield put(stopLoad());
  }
}
